import { CONSENT_MESSAGES } from "./constants";
import { getProviderProfile } from "./provider";
import { skipsEnrollmentOtp } from "./program";
import type {
  ConsentEnforcement,
  ConsentOutcome,
  LoyaltyProvider,
  ProgramDetailsEnvelope,
} from "./types";

/**
 * Customer consent to a loyalty programme — whether it is owed, who enforces it, what the
 * backend just said, and what to record.
 *
 * Pure, and deliberately independent of the customer FORM. Nothing here knows about
 * `react-hook-form`, a modal, or a two-step wizard; the caller passes the customer payload in and
 * decides what to do with the outcome. That is what makes the same rules usable from the
 * customer modal, an add-customer screen, the kiosk or mobile.
 *
 * The two paths this module exists to keep straight (`docs/flows/LOYALTY.md`):
 *
 * | | OTP path | OTP-less path |
 * |---|---|---|
 * | picked by | `skipEnrollmentOtp` false | `skipEnrollmentOtp` true |
 * | "Send OTP" CTA | shown | hidden |
 * | customer-step CTA | disabled until consent is recorded | enabled |
 * | consent call fires on | the Send OTP click | the CTA click |
 * | consent recorded by | the verify call | the send call itself |
 */

/** Everything the "is consent owed?" question depends on. */
export interface ConsentContext {
  /** `businessData.isCustomerConsentMandatory`. */
  isMandatoryForBusiness?: boolean;
  activeProvider?: LoyaltyProvider | null;
  programDetails?: ProgramDetailsEnvelope | null;
  /** `externalLoyaltyData.loyaltyInfo` — a blocked customer cannot be enrolled. */
  customerLoyaltyInfo?: { isBlocked?: boolean } | null;
}

/**
 * Is consent mandatory for this merchant, provider and customer right now?
 *
 * 🔴 **First question is whether the PARTNER has a consent flow at all**
 * (`supportsEnrollmentConsent`). Only dotpe and xeno are integrated on the backend; for anyone
 * else the enrol toggle and its OTP ask the cashier for something no endpoint can honour, so the
 * whole affordance is off however the merchant has configured the setting. This is the single
 * gate — the consent block, the cart strip, the kiosk prompt and `buildProviderConsents` all
 * reach it through here.
 *
 * ⚠️ A provider whose profile also sets `requiresLiveProgramForConsent` adds two conditions of its
 * own: the programme has to be live for the channel, and the customer must not be blocked by the
 * provider. Every other supported partner needs only the merchant setting and an active provider.
 */
export function isConsentMandatory({
  isMandatoryForBusiness,
  activeProvider,
  programDetails,
  customerLoyaltyInfo,
}: ConsentContext): boolean {
  const profile = getProviderProfile(activeProvider?.metaId);
  return Boolean(
    isMandatoryForBusiness &&
      activeProvider &&
      profile.supportsEnrollmentConsent &&
      (!profile.requiresLiveProgramForConsent ||
        (programDetails?.data?.status && !customerLoyaltyInfo?.isBlocked)),
  );
}

/**
 * This customer's recorded answer for this provider: `true`, `false`, or `undefined` when we hold
 * no answer at all. The three are genuinely different — see `resolveEnrolToggle`.
 */
export function getConsentForProvider(
  providerConsents: Record<string, boolean> | null | undefined,
  providerMetaId?: string,
): boolean | undefined {
  if (!providerConsents || !providerMetaId) return undefined;
  return providerConsents[providerMetaId];
}

/**
 * A customer who predates the consent feature: we hold a record for them, but no consent map at
 * all. They are never asked — there is no answer to collect retrospectively, and prompting every
 * returning customer would block the till.
 */
export const isPreConsentCustomer = (
  hasCustomerRecord: boolean,
  providerConsents: Record<string, boolean> | null | undefined,
): boolean => hasCustomerRecord && !providerConsents;

/**
 * Where the enrol toggle should start.
 *
 * An explicit `false` from a customer WE HOLD A RECORD FOR wins over everything: they declined,
 * that is a known answer, and it is not re-asked by defaulting the switch back on.
 *
 * 🔴 **`hasCustomerRecord` is what makes that `false` an answer at all.** For a customer absent
 * from our DB the map did not come from us — the customer saga fetches `?external=true` and
 * stores the PROVIDER's view, where `false` means "not enrolled with me yet", not "declined".
 * Reading it as a decline seeds a brand-new customer's switch off and no default can reach them.
 * `buildProviderConsents` already draws this line the same way: `isNewCustomer` starts a fresh
 * map rather than honouring the incoming one.
 *
 * 🔴 **`skipsOtp` overrides the merchant's DEFAULT, never a recorded decline.** dotpe
 * with the programme's `skipEnrollmentOtp` starts the switch on for a customer we hold no answer
 * for, `customerConsentDefaultOff` included — but on that path nothing is asked before the CTA
 * enrols (`enforcement.enrolOnNext`), so a decliner it seeded back on would be re-enrolled with
 * no one prompted. That is why this check sits BELOW the decline and not above it. Pass it only
 * when the pairing genuinely holds — `skipsOtp: skipsEnrollmentOtp(providerMetaId, programDetails)`.
 * The parameter is deliberately NOT called `skipsEnrollmentOtp`: this module imports the predicate
 * of that name, and a parameter shadowing it would read identically while always being truthy.
 *
 * Otherwise a known customer is treated as enrolled and a new one follows the merchant's default
 * (`customerConsentDefaultOff` inverted).
 */
export function resolveEnrolToggle({
  providerConsents,
  providerMetaId,
  hasCustomerRecord,
  defaultOn,
  skipsOtp = false,
}: {
  providerConsents: Record<string, boolean> | null | undefined;
  providerMetaId?: string;
  hasCustomerRecord: boolean;
  defaultOn: boolean;
  skipsOtp?: boolean;
}): boolean {
  if (hasCustomerRecord && getConsentForProvider(providerConsents, providerMetaId) === false)
    return false;
  if (skipsOtp) return true;
  if (!hasCustomerRecord) return defaultOn;
  return true;
}

/** Everything the "is consent owed *on this screen*?" question depends on. */
export interface ConsentRequirementInput {
  isMandatory: boolean;
  hasCustomerRecord: boolean;
  providerConsents: Record<string, boolean> | null | undefined;
  providerMetaId?: string;
  /** The cashier's enrol toggle. */
  enrolCustomer: boolean;
  /** Consent recorded during THIS session of the screen. */
  consentProvidedNow: boolean;
  /** Whether the customer step is actually on screen — consent is only enforced while it is. */
  isCustomerStepVisible: boolean;
  isOffline: boolean;
}

/**
 * Is consent owed but not yet recorded?
 *
 * Offline is excluded because the consent APIs are server-backed; a terminal that cannot reach
 * them must not be able to block a sale on them.
 */
export function isConsentRequired({
  isMandatory,
  hasCustomerRecord,
  providerConsents,
  providerMetaId,
  enrolCustomer,
  consentProvidedNow,
  isCustomerStepVisible,
  isOffline,
}: ConsentRequirementInput): boolean {
  return Boolean(
    isMandatory &&
      !isPreConsentCustomer(hasCustomerRecord, providerConsents) &&
      enrolCustomer &&
      !consentProvidedNow &&
      !getConsentForProvider(providerConsents, providerMetaId) &&
      isCustomerStepVisible &&
      !isOffline,
  );
}

/**
 * Given that consent is owed, who enforces it — the OTP modal, or the CTA?
 *
 * One predicate says consent is owed; this decides which of the two columns in the table above
 * applies. Splitting them is what lets a screen enforce consent without knowing why it is owed.
 */
export function getConsentEnforcement(
  consentRequired: boolean,
  providerMetaId?: string,
  programDetails?: ProgramDetailsEnvelope | null,
): ConsentEnforcement {
  const skipOtp = skipsEnrollmentOtp(providerMetaId, programDetails);
  return {
    blocksSubmit: consentRequired && !skipOtp,
    enrolOnNext: consentRequired && skipOtp,
    showsSendOtpCta: !skipOtp,
  };
}

/**
 * What `customer/consent-otp` said, on the path where an OTP is expected.
 *
 * `CUSTOMER_ALREADY_EXISTS` is prose rather than a code, but it is the backend's way of saying
 * this customer is already enrolled, and treating it as a failure would show an error toast over
 * a success.
 */
export function classifyConsentOtpResponse(res: any): ConsentOutcome {
  if (res?.status && res?.message === CONSENT_MESSAGES.ALREADY_CONSENTED) return "already-consented";
  if (res?.status) return "otp-sent";
  if (res?.message === CONSENT_MESSAGES.CUSTOMER_EXISTS) return "already-consented";
  return "failed";
}

/**
 * What `customer/consent-otp` said, on the OTP-LESS path.
 *
 * ⚠️ `otpSent: true` is a real branch, not a defensive one: the backend can decide an OTP is
 * needed after all, and the caller must then fall back to the OTP modal and hold whatever action
 * it was about to take. On this path the call itself is what enrols, so a plain `status: true`
 * means done.
 */
export function classifyEnrolWithoutOtpResponse(res: any): ConsentOutcome {
  if (res?.status && res?.otpSent) return "otp-sent";
  if (res?.status || res?.message === CONSENT_MESSAGES.CUSTOMER_EXISTS) return "enrolled";
  return "failed";
}

/** What `customer/consent-otp/verify` said. */
export function classifyConsentVerifyResponse(res: any): ConsentOutcome {
  if (res?.status && res?.message === CONSENT_MESSAGES.ALREADY_CONSENTED) return "already-consented";
  if (res?.status) return "enrolled";
  return "failed";
}

/**
 * The `providerConsents` map to save with the customer.
 *
 * A new customer starts a fresh map. An existing one keeps theirs, and is only written to when
 * this provider is not already a recorded `true` — a granted consent is never downgraded by a
 * later save. Returns the map unchanged (or `undefined`) when consent does not apply here, so
 * the caller can pass the result straight through.
 *
 * ⚠️ Clones rather than mutating: the input comes from the redux store.
 */
export function buildProviderConsents({
  existing,
  providerMetaId,
  isNewCustomer,
  consentProvided,
  applies,
}: {
  existing: Record<string, boolean> | null | undefined;
  providerMetaId?: string;
  isNewCustomer: boolean;
  consentProvided: boolean;
  /** `isConsentMandatory && !isOffline` — whether consent is in play for this save at all. */
  applies: boolean;
}): Record<string, boolean> | undefined {
  const next = existing ? { ...existing } : undefined;
  if (!applies || !providerMetaId) return next;

  if (isNewCustomer) return { [providerMetaId]: consentProvided };
  if (next && next[providerMetaId] !== true) next[providerMetaId] = consentProvided;
  return next;
}
